import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import './EventEdit.css';
import './CourseView.css';
import './CourseEdit.css';

function EventEdit() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { getEvent, getEventTypes, updateEvent, clearCache } = useData();

  const [event, setEvent] = useState(null);
  const [endeavor, setEndeavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [serverEventTypes, setServerEventTypes] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventTypeId: null,
    eventType: '',
    startDatetime: '', // datetime-local value (YYYY-MM-DDTHH:mm)
    endDatetime: '',
    location: '',
  });

  // Helpers to convert between input datetime-local and server naive datetime strings
  const toInputDateTime = useCallback((val) => {
    if (!val) return '';
    // If it's already in YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm, strip seconds
    const m = String(val).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/);
    if (m) return m[1];
    // Fallback: parse date and format local time without timezone
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
      return '';
    }
  }, []);

  const toServerDateTime = useCallback((val) => {
    if (!val) return null;
    // Expect input like YYYY-MM-DDTHH:mm or maybe with seconds
    const m = String(val).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/);
    if (m) return `${m[1]}:00`;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    } catch (e) {
      return null;
    }
  }, []);

  // Fetch event and event types on mount
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [evtResp, types] = await Promise.all([getEvent(eventId), getEventTypes()]);
        // getEvent may return an object or a Response-like; handle both
        const evt = evtResp && evtResp.json ? await evtResp.json() : evtResp;
        if (!mounted) return;
        setEvent(evt || null);
        setServerEventTypes(Array.isArray(types) ? types : (types && types.json ? await types.json() : []));
        if (evt) {
          setFormData(f => ({
            ...f,
            name: evt.title || evt.name || '',
            description: evt.description || '',
            eventTypeId: evt.eventTypeId || evt.event_type_id || evt.event_type || null,
            eventType: evt.eventType || evt.event_type || '',
            startDatetime: toInputDateTime(evt.startDatetime || evt.start_datetime || evt.startDate || evt.start_date || evt.start),
            endDatetime: toInputDateTime(evt.endDatetime || evt.end_datetime || evt.endDate || evt.end_date || evt.end),
            location: evt.location || ''
          }));
          if (evt.endeavor) setEndeavor(evt.endeavor);
          if (evt.endeavorId) setEndeavor({ id: evt.endeavorId });
        }
      } catch (e) {
        console.error('EventEdit: error loading event/types', e);
        setError('Unable to load event');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [eventId, getEvent, getEventTypes, toInputDateTime]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target || {};
    setFormData(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const validationErrors = useMemo(() => {
    const errors = {};
    if (!formData.name || !String(formData.name).trim()) errors.name = 'Event name is required';
    if (!formData.startDatetime) errors.startDatetime = 'Start date and time is required';
    if (formData.startDatetime && formData.endDatetime) {
      try {
        const start = new Date(formData.startDatetime);
        const end = new Date(formData.endDatetime);
        if (end <= start) errors.endDatetime = 'End must be after start';
      } catch (e) { /* ignore */ }
    }
    return errors;
  }, [formData]);

  const isFormValid = useMemo(() => Object.keys(validationErrors).length === 0, [validationErrors]);

  const handleSave = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!isFormValid) { setError('Please fix validation errors'); return; }
    setSaving(true);
    setError(null);
    try {
      const updateData = {
        eventId: Number(eventId),
        title: formData.name.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        eventTypeId: formData.eventTypeId || undefined,
        eventType: formData.eventType || undefined,
        startDatetime: toServerDateTime(formData.startDatetime),
        endDatetime: toServerDateTime(formData.endDatetime),
        location: formData.location && String(formData.location).trim() ? formData.location.trim() : undefined,
        endeavorId: event && event.endeavorId ? event.endeavorId : undefined,
      };

      // Strip undefined/null
      const sanitized = Object.keys(updateData).reduce((acc, k) => {
        const v = updateData[k];
        if (v !== null && typeof v !== 'undefined') acc[k] = v;
        return acc;
      }, {});

      try { console.log('EventEdit: Outbound (sanitized) JSON:', JSON.stringify(sanitized)); } catch (e) { /* ignore */ }

      const changedFields = Object.keys(sanitized).reduce((acc, k) => {
        try { const before = event && event[k]; if (String(before) !== String(sanitized[k])) acc[k] = { before, after: sanitized[k] }; } catch (e) {}
        return acc;
      }, {});
      console.log('EventEdit: Fields changed:', changedFields);

      const response = await updateEvent(eventId, sanitized);

      if (!response || !response.ok) {
        let body = '<no-response-body>';
        try { body = await (response && response.text ? response.text() : Promise.resolve(body)); } catch (er) {}
        throw new Error(`Failed to update event: ${response ? response.status : '<no-response>'} - ${body}`);
      }

      try {
        const respBody = await response.json();
        console.log('EventEdit: updateEvent response body:', respBody);
      } catch (e) { console.log('EventEdit: updateEvent returned no JSON body'); }

      // Clear caches
      clearCache('event');
      clearCache('user-endeavors');
      if (event && event.endeavorId) clearCache(`endeavor_${event.endeavorId}`);

      navigate(`/event/${eventId}`);
    } catch (err) {
      console.error('EventEdit: error saving', err);
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [eventId, formData, event, updateEvent, clearCache, navigate, isFormValid, toServerDateTime]);

  const getEventTypeIcon = useCallback((type) => {
    const map = new Map([
      ['lecture', '🎓'], ['class','🎓'], ['lab','🔬'], ['discussion','💬'], ['exam','📝'], ['quiz','❓'], ['office_hours','🏢'], ['meeting','👥'], ['workshop','🔧'], ['presentation','📊'], ['field_trip','🚌'], ['guest_speaker','🎤'], ['other','📅']
    ]);
    return map.get(String(type || '').toLowerCase()) || '📅';
  }, []);

  if (loading) return (
    <div className="event-edit-container"><div className="loading-state"><div className="loading-spinner"/> <p>Loading event...</p></div></div>
  );

  if (!event) return (
    <div className="event-edit-container"><div className="error-state"><div className="error-icon">📅</div><h2>Event Not Found</h2><p>The event you're trying to edit doesn't exist.</p><button className="btn btn-secondary" onClick={() => navigate(-1)}>Go Back</button></div></div>
  );

  return (
    <div className="event-edit-container">
      <button className="back-btn" onClick={() => navigate(`/event/${eventId}`)} aria-label="Back to Event">← Back to Event</button>

      <div className="event-edit-header">
        <div className="event-icon-container"><div className="event-icon">{getEventTypeIcon(formData.eventType)}</div></div>
        <div className="header-content">
          <h1>Edit Event</h1>
          <p className="event-type-label">{(serverEventTypes.find(t => Number(t.eventTypeId || t.id || t.event_type_id) === Number(formData.eventTypeId))?.name) || formData.eventType || 'Event'}</p>
          {endeavor && (<span className="endeavor-link">{endeavor.name || endeavor.id}</span>)}
        </div>
      </div>

      <div className="event-edit-content">
        <div className="event-edit-form">
          <h2><span className="section-icon">✏️</span>Event Details</h2>

          {error && <div className="error-alert"><span className="error-icon">⚠️</span>{error}</div>}

          <div className="form-group">
            <label htmlFor="name">EVENT NAME <span className="required">*</span></label>
            <input id="name" name="name" type="text" value={formData.name} onChange={handleInputChange} placeholder="Enter event name" className={validationErrors.name ? 'error' : ''} />
            {validationErrors.name && <span className="field-error">{validationErrors.name}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventTypeId">EVENT TYPE</label>
              <select id="eventTypeId" name="eventTypeId" value={formData.eventTypeId || ''} onChange={handleInputChange}>
                <option value="">Select an event type...</option>
                {serverEventTypes.map(type => (
                  <option key={type.eventTypeId || type.id || type.event_type_id} value={type.eventTypeId || type.id || type.event_type_id}>{type.name || type.label || type.eventTypeName}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="location">LOCATION</label>
              <input id="location" name="location" type="text" value={formData.location} onChange={handleInputChange} placeholder="Enter location" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDatetime">START DATE & TIME <span className="required">*</span></label>
              <input id="startDatetime" name="startDatetime" type="datetime-local" value={formData.startDatetime} onChange={handleInputChange} className={validationErrors.startDatetime ? 'error' : ''} />
              {validationErrors.startDatetime && <span className="field-error">{validationErrors.startDatetime}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDatetime">END DATE & TIME</label>
              <input id="endDatetime" name="endDatetime" type="datetime-local" value={formData.endDatetime} onChange={handleInputChange} className={validationErrors.endDatetime ? 'error' : ''} />
              {validationErrors.endDatetime && <span className="field-error">{validationErrors.endDatetime}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">DESCRIPTION</label>
            <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Enter event description" rows={4} />
          </div>

        </div>
      </div>

      <div className="sticky-footer">
        <div className="sticky-inner">
          <div className="left-actions" />
          <div className="right-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !isFormValid}>{saving ? (<><div className="loading-spinner small"/>Saving...</>) : '💾 Save All Changes'}</button>
            <button className="btn btn-secondary" onClick={() => navigate(`/event/${eventId}`)} disabled={saving}>Cancel & Return</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventEdit;

